import {
  getCssSize,
  Severity,
  ThemeIconKey,
  Icon,
  DefaultFoundationTheme,
  ThemeColorPath,
} from 'platform/foundation';
import {css, useTheme} from 'styled-components';

import {MouseEventHandler, RefObject} from 'react';

import {suffixTestId, TestIdProps} from 'shared/web';

export type IconButtonPriority = 'primary' | 'secondary' | 'tertiary' | 'default';

export interface IconButtonProps extends TestIdProps {
  ref?: RefObject<HTMLButtonElement>;
  icon: ThemeIconKey;
  isDisabled?: boolean;
  severity?: Severity;
  size?: 'small' | 'default';
  iconSize?: 'default' | 'large';
  priority?: IconButtonPriority;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  tabIndex?: number;
  'aria-label'?: string;
  'aria-controls'?: string;
}

export function IconButton(props: IconButtonProps) {
  const theme = useTheme();

  const size = getCssSize('small' === props.size ? 6 : 8);
  const iconSize = 'large' === props.iconSize ? 5 : 4;

  return (
    <button
      data-testid={suffixTestId('iconButton', props)}
      onClick={props.onClick}
      disabled={props.isDisabled}
      aria-label={props['aria-label']}
      aria-controls={props['aria-controls']}
      type="button"
      tabIndex={props.tabIndex}
      css={css`
        width: ${size};
        height: ${size};
        display: flex;
        flex-shrink: 0;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        border-radius: ${theme.radii.small};
        pointer-events: ${props.isDisabled ? 'none' : 'all'};
        transition: background-color 0.2s ease-in-out;
        ${getColorStyle(theme, props.priority, props.severity)}
      `}
    >
      <Icon
        color={getIconColor(props.priority, props.severity)}
        value={props.icon}
        size={iconSize}
      />
    </button>
  );
}

const getColorStyle = (
  theme: DefaultFoundationTheme,
  priority?: IconButtonPriority,
  severity?: Severity
) => {
  switch (priority) {
    case 'primary':
      return css`
        background-color: ${severity
          ? theme.colors.severity[severity]
          : theme.colors.general.accent};
        &:hover {
          background-color: ${severity
            ? theme.colors.severity.hover[severity]
            : theme.colors.palettes.blue[70][100]};
        }
        &:active {
          background-color: ${severity
            ? theme.colors.severity.active[severity]
            : theme.colors.palettes.blue[80][100]};
        }
        &:disabled {
          opacity: 0.4;
        }
      `;

    case 'secondary':
      return css`
        background-color: ${theme.colors.palettes.neutral[30][100]};
        &:hover {
          background-color: ${theme.colors.palettes.neutral[40][100]};
        }
        &:active {
          background-color: ${theme.colors.palettes.neutral[50][100]};
        }
        &:disabled {
          opacity: 0.4;
        }
      `;

    case 'tertiary':
      return css`
        background-color: ${theme.colors.palettes.white};
        border: 1px solid
          ${severity
            ? theme.colors.severity.active[severity]
            : theme.colors.palettes.neutral[70][100]};
        &:hover {
          border-color: ${severity
            ? theme.colors.severity.active[severity]
            : theme.colors.palettes.neutral[200][100]};
        }
        &:active {
          border-color: ${severity
            ? theme.colors.severity.active[severity]
            : theme.colors.palettes.neutral[900][100]};
        }
        &:disabled {
          opacity: 0.4;
        }
      `;

    default:
      return css`
        background-color: transparent;
        &:hover {
          background-color: ${theme.colors.palettes.neutral[30][100]};
        }
        &:active {
          background-color: ${theme.colors.palettes.neutral[40][100]};
        }
        &:disabled {
          opacity: 0.4;
        }
      `;
  }
};

const getIconColor = (priority?: IconButtonPriority, severity?: Severity): ThemeColorPath => {
  if ('primary' === priority) {
    return 'general.white';
  }
  return severity ? `severity.${severity}` : 'text.primary';
};
